import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, Settings2 } from "lucide-react";
import { Button } from "./ui/button";
import { initAnalytics } from "../lib/analytics";
import { initTracking, trackPageView } from "../lib/tracking";

const STORAGE_KEY = "fg_cookie_consent_v1";

/**
 * Routes where the cookie banner must NOT render. Admin pages have
 * sticky save bars + composer modals that would be obscured by a
 * bottom-right cookie banner. The admin is authenticated and the
 * privacy/consent prompt belongs on the public surface only.
 */
const HIDE_ON_PATH_PREFIXES = ["/admin"];

/** Read current consent. Returns 'all' | 'essential' | null (undecided). */
export function readConsent() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Programmatic helper for opening the banner from elsewhere (e.g. footer link). */
export function openCookieSettings() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent("fg:cookie-banner-reopen"));
  } catch {
    /* ignore */
  }
}

export default function CookieBanner() {
  const [show, setShow] = useState(false);
  const location = useLocation();
  const onHiddenRoute = HIDE_ON_PATH_PREFIXES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (onHiddenRoute) {
      setShow(false);
      return undefined;
    }
    const decision = readConsent();
    if (!decision) {
      // Slight delay so banner doesn't compete with the hero-load animation
      const t = setTimeout(() => setShow(true), 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [onHiddenRoute]);

  useEffect(() => {
    const reopen = () => {
      if (onHiddenRoute) return;
      setShow(true);
    };
    window.addEventListener("fg:cookie-banner-reopen", reopen);
    return () => window.removeEventListener("fg:cookie-banner-reopen", reopen);
  }, [onHiddenRoute]);

  const accept = (level) => {
    try {
      localStorage.setItem(STORAGE_KEY, level);
    } catch {
      /* ignore */
    }
    if (level === "all") {
      // Initialise analytics now that consent is granted
      initAnalytics();
      // Bootstrap the unified tracking facade (GA4 + any registered pixels)
      // and fire the page_view for the route we are currently on so the
      // first session-engagement signal isn't lost.
      initTracking();
      try {
        trackPageView(window.location.pathname, window.location.search);
      } catch { /* swallow */ }
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 px-3 sm:px-4 pb-3 sm:pb-4 sm:left-auto sm:right-4 sm:max-w-md pointer-events-none"
      role="region"
      aria-live="polite"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-body"
      data-testid="cookie-banner"
    >
      <div className="rounded-2xl border border-emerald-100 bg-white shadow-2xl p-4 sm:p-5 pointer-events-auto">
        <div className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-amber-100 text-amber-700 shrink-0" aria-hidden="true">
            <Cookie className="w-4 h-4" />
          </span>
          <div className="flex-1">
            <h2 id="cookie-banner-title" className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold m-0">
              Cookies on Feel Greats
            </h2>
            <p id="cookie-banner-body" className="mt-1 text-sm text-emerald-900/80 leading-relaxed">
              We use essential cookies to keep you signed in, and optional analytics cookies to
              improve how Feel Greats works for you. See our{" "}
              <Link
                to="/privacy"
                className="underline text-emerald-700 hover:text-emerald-800"
                data-testid="cookie-banner-privacy-link"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => accept("all")}
            className="cta-shine rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-2.5 text-sm font-semibold flex-1 sm:flex-none"
            data-testid="cookie-accept-all"
          >
            Accept all
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => accept("essential")}
            className="rounded-full border-emerald-200 text-emerald-900 hover:bg-emerald-50 px-5 py-2.5 text-sm font-semibold flex-1 sm:flex-none"
            data-testid="cookie-essential-only"
          >
            Essential only
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Tiny inline link component for the footer to reopen the chooser. */
export function CookieSettingsLink({ className = "" }) {
  return (
    <button
      type="button"
      onClick={openCookieSettings}
      className={`inline-flex items-center gap-1 hover:text-emerald-700 transition-colors ${className}`}
      data-testid="footer-cookie-settings-link"
    >
      <Settings2 className="w-3 h-3" /> Cookie settings
    </button>
  );
}
